import React, { createContext, useContext, useEffect, useState } from 'react';
import messages from './messages.json';
import './layout.css';

export const languages = ['ko', 'en', 'ja', 'zh-CN', 'vi', 'th', 'de', 'fr'] as const;
export type Language = typeof languages[number];
const names: Record<Language, string> = {
  ko: '한국어', en: 'English', ja: '日本語', 'zh-CN': '简体中文',
  vi: 'Tiếng Việt', th: 'ไทย', de: 'Deutsch', fr: 'Français'
};
const storageKey = 'socfai.ui.language';
const isLanguage = (value: unknown): value is Language =>
  typeof value === 'string' && (languages as readonly string[]).includes(value);
function initialLanguage(): Language {
  try {
    const saved = localStorage.getItem(storageKey);
    return isLanguage(saved) ? saved : 'ko';
  } catch { return 'ko'; }
}
let currentLanguage: Language = initialLanguage();
const catalog = messages as Record<string, Record<Language, string>>;
const LanguageContext = createContext<Language>('ko');
export const useLanguage = () => useContext(LanguageContext);
const SetLanguageContext = createContext<((language: Language) => void) | null>(null);

export function LanguageSelector({ login = false }: { login?: boolean }) {
  const language = useLanguage();
  const setLanguage = useContext(SetLanguageContext);
  return (
    <div className={`socfai-language-control${login ? ' socfai-language-control-login' : ''}`}>
      <select id="socfai-language" data-testid="language-select" aria-label={t('Language')}
        value={language} disabled={!setLanguage}
        onChange={event => setLanguage?.(event.target.value as Language)}>
        {languages.map(code => <option key={code} value={code} lang={code}>{names[code]}</option>)}
      </select>
      <span className="socfai-language-code" aria-hidden="true">
        {language === 'zh-CN' ? 'ZH' : language.toUpperCase()}
      </span>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m7 10 5 5 5-5z" /></svg>
    </div>
  );
}

// Call only at an explicit display boundary. Business values, JSON and requests
// never pass through this function. Interpolation does not format its inputs.
export function t(source: string, values: readonly unknown[] = []): string {
  const key = source.replace(/\s+/g, ' ').trim();
  const translated = catalog[key]?.[currentLanguage] ?? source;
  return translated.replace(/\{(\d+)\}/g, (match, index) =>
    Number(index) < values.length ? String(values[Number(index)]) : match);
}
export function ui(value: unknown): any {
  return typeof value === 'string' ? t(value) : value;
}
export function uiError(value: unknown): string {
  if (!value) return '';
  const text = String(value);
  if (catalog[text.replace(/\s+/g, ' ').trim()]) return t(text);
  // Unknown server exceptions are not translated by an external service and
  // are not exposed as raw private diagnostic messages.
  return t('The request failed. Please try again or contact the administrator.');
}
export function paginationLabel(type: string, page: number | null, selected: boolean): string {
  if (type === 'page') return t(selected ? 'Current page {0}' : 'Go to page {0}', [page ?? '']);
  const labels: Record<string, string> = {
    first: 'First page', last: 'Last page', next: 'Next page', previous: 'Previous page'
  };
  return t(labels[type] || 'Pagination');
}
export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguage] = useState<Language>(currentLanguage);
  currentLanguage = language;
  useEffect(() => {
    document.documentElement.lang = language;
    document.title = t('SOCFAI Blockchain Management');
    try { localStorage.setItem(storageKey, language); } catch { /* in-memory selection still works */ }
  }, [language]);
  useEffect(() => {
    const restore = (event: StorageEvent) => {
      if (event.key === storageKey && isLanguage(event.newValue)) setLanguage(event.newValue);
    };
    window.addEventListener('storage', restore);
    return () => window.removeEventListener('storage', restore);
  }, []);
  return (
    <LanguageContext.Provider value={language}>
      <SetLanguageContext.Provider value={setLanguage}>
        {children}
      </SetLanguageContext.Provider>
    </LanguageContext.Provider>
  );
}
